import { type GraphQLResult } from './apiClient';
import { PropListResponse } from '../types/prop';
import { getApiClient } from './apiClient';

const getPropsQuery = /* GraphQL */ `
  query GetProps($limit: Int, $offset: Int, $keyword: String) {
    getPropList(limit: $limit, offset: $offset, keyword: $keyword) {
      props {
        no
        phoneVersion
        propFile
        propKey
        propValue
        note
        status
      }
      total
    }
  }
`;

const importPropsMutation = /* GraphQL */ `
  mutation ImportProps($props: [BuildProp]!) {
    importProp(props: $props) {
      result
      message
    }
  }
`;

const removePropsMutation = /* GraphQL */ `
  mutation RemoveProps($ids: [String]!) {
    removeProp(ids: $ids) {
      result
      message
    }
  }
`;

const updatePropMutation = /* GraphQL */ `
  mutation UpdateProp($no: Int!, $prop: BuildProp!) {
    updateProp(no: $no, prop: $prop) {
      result
      message
    }
  }
`;

interface GetPropsQueryResponse {
  getPropList: PropListResponse;
}

interface ImportPropsResponse {
  importProp: {
    result: boolean;
    message: string;
  };
}

interface RemovePropsResponse {
  removeProp: {
    result: boolean;
    message: string;
  };
}

interface UpdatePropResponse {
  updateProp: {
    result: boolean;
    message: string;
  };
}

export const propService = {
  async getProps(params: {
    limit?: number;
    page?: number;
    searchTerm?: string;
  }): Promise<PropListResponse> {
    try {
      const limit = params.limit;
      const page = params.page ?? 0;
      const offset = (limit ?? 10) * page;
      const keyword = params.searchTerm ?? '';
      const response = await getApiClient().graphql({
        query: getPropsQuery,
        variables: {
          limit,
          offset,
          keyword
        }
      }) as GraphQLResult<GetPropsQueryResponse>;

      if (response.errors) {
        throw new Error(response.errors.map(e => e.message).join(', '));
      }

      if (!response.data) throw new Error('No data returned');
      return response.data.getPropList;
    } catch (error) {
      console.error('Error in getProps:', error);
      throw error;
    }
  },

  async importProps(props: any[]) {
    try {
      const response = await getApiClient().graphql({
        query: importPropsMutation,
        variables: {
          props
        }
      }) as GraphQLResult<ImportPropsResponse>;

      if (response.errors) {
        throw new Error(response.errors.map(e => e.message).join(', '));
      }

      if (!response.data) throw new Error('No data returned');
      return response.data.importProp;
    } catch (error) {
      console.error('Error in importProps:', error);
      throw error;
    }
  },

  async removeProps(ids: string[]) {
    try {
      const response = await getApiClient().graphql({
        query: removePropsMutation,
        variables: {
          ids
        }
      }) as GraphQLResult<RemovePropsResponse>;

      if (response.errors) {
        throw new Error(response.errors.map(e => e.message).join(', '));
      }

      if (!response.data) throw new Error('No data returned');
      return response.data.removeProp;
    } catch (error) {
      console.error('Error in removeProps:', error);
      throw error;
    }
  },

  async updateProp(no: string, prop: {
    phoneVersion: string;
    propFile: string;
    propKey: string;
    propValue: string;
    note: string;
    status: string | number;
  }) {
    try {
      const response = await getApiClient().graphql({
        query: updatePropMutation,
        variables: {
          no: Number(no),
          prop: {
            ...prop,
            status: Number(prop.status) || 1
          }
        }
      }) as GraphQLResult<UpdatePropResponse>;

      if (response.errors) {
        throw new Error(response.errors.map(e => e.message).join(', '));
      }

      if (!response.data) throw new Error('No data returned');
      return response.data.updateProp;
    } catch (error) {
      console.error('Error in updateProp:', error);
      throw error;
    }
  }
};